import { Fragment, useEffect, useRef, useState, type ReactNode } from "react";
import {
  matchContactNames,
  nameParts,
  parseVcf,
  relationContact,
  searchByName,
  type ContactCard,
  type GroupPerson,
  type OwnerLinked,
  type OwnerNewParticipantsResponse,
  type OwnerWaiting,
  type ParentMatch,
} from "@teamna/shared";
import {
  useAskParents,
  useCleanGroupNames,
  useDismissParentMatch,
  useNameGroupPeople,
  useNameGroupPerson,
  useNewParticipants,
  useRunParentMatches,
} from "@teamna/web-client/hooks/use-coach-participants.ts";
import { useInviteFamilies } from "@teamna/web-client/hooks/use-families.ts";
import { track } from "@teamna/web-client/analytics/web.ts";
import { useLabels } from "@teamna/web-client/hooks/use-labels.ts";
import { useTrackNameSearch } from "@teamna/web-client/hooks/use-name-search.ts";
import { useSyncNow, useWhatsAppSync } from "@teamna/web-client/hooks/use-whatsapp-sync.ts";
import { formatDateTime } from "@teamna/web-client/lib/format.ts";
import { messageForError } from "../components/CodeFlow.tsx";
import { Avatar, AvatarName, Button, CellField, ConfirmDialog, EmptyState, ErrorState, FileField, Notice, SearchField, SkeletonList, Tabs, panelId, showToast, tabId } from "../components/index.ts";
import { useStaffPhotos } from "./staff-photos.ts";
import { phoneText } from "./families/model.ts";
import { LinkProgressBar } from "./link-progress.tsx";
import { AskStep, REMINDER_MESSAGE, claimPairs, claimsByName, fullGroupMessage } from "./new-participants-ask.tsx";
import { ParentPicker, PickRow, WaitingCard, personLabel, useLinkWithUndo } from "./new-participants-cards.tsx";
import { SwimmersPanel } from "./new-participants-panel.tsx";
import { OfficePage, runsBusiness } from "./office.tsx";
import { WhatsAppSyncPanel } from "./whatsapp-sync/sync-tool.tsx";

/**
 * `/owner/new-participants`: link the names coaches added to parents, starting from the parents'
 * WhatsApp group (strategy repo: teamna/prototypes/link-parents-sync.html, agreed 2026-10-11).
 *
 *   1. Sync the group: the people of the parents' WhatsApp group, in three lists (with a name, a
 *      number only, the team left out). The AI cleans the WhatsApp names once ("i$mAil" is Ismail).
 *      "Sync now" reads a connected group again; the WhatsApp sync tool connects a new one.
 *   2. Name the numbers: the owner types a name for the people WhatsApp shows as a number only.
 *   3. AI matches (Matches without a model): Teamna reads the names and finds the likely parent of each name. Siblings move
 *      together. The owner taps Link, "Link all likely", "Not right", or "Not siblings".
 *   4. Ask the parents: one message the owner posts in the group, with a link where a parent finds
 *      their child; the answers come back here, and the owner links them or says No (migration 0045).
 *   5. Still missing: every name with no parent yet, with what Teamna knows and every way to link.
 *
 * The bar on top (and on Home) counts the names linked and the names that wait. Every link can be
 * taken back: Undo on the toast, or Unlink in the Swimmers panel.
 */
export function OwnerNewParticipantsScreen() {
  const { label } = useLabels();
  const participants = label("participant", { plural: true });
  return (
    <OfficePage
      title={`New ${participants}`}
      description={`Your ${label("staff", { plural: true })} added these names. Find each ${label("member")} and link them. ${label("member", { plural: true, capital: true })} get no message until you invite them.`}
    >
      {(business) =>
        runsBusiness(business) ? (
          // Keyed by business: a switch to another business starts its own steps and asks (review F2.1).
          <NewParticipants key={business.businessId} businessId={business.businessId} businessName={business.name} />
        ) : (
          <EmptyState title="Owner and admin only" message="Ask the owner to link these names." />
        )
      }
    </OfficePage>
  );
}

type Step = "sync" | "name" | "matches" | "ask" | "missing";

const unnamed = (p: GroupPerson) => p.nameKind !== "named" && !p.nameEdited;

/**
 * The step the page opens on: the earliest one the owner has not done (review F8.1). Naming comes
 * only before the first run of the matches, so a group with numbers nobody knows does not open on
 * it on every visit.
 */
function firstStep(data: OwnerNewParticipantsResponse): Step {
  if (data.people.length === 0) return "sync";
  if (!data.matcher.lastRunAt) return data.people.some(unnamed) ? "name" : "matches";
  // Any open match is work, a sibling grouping too (review F13.2).
  if (data.matches.length > 0) return "matches";
  // Answers of the parents to check (migration 0045).
  if (data.claims.some((c) => c.status === "new")) return "ask";
  return "missing";
}

function NewParticipants({ businessId, businessName }: { businessId: string; businessName: string }) {
  const { label } = useLabels();
  const list = useNewParticipants(businessId);
  const [step, setStep] = useState<Step | null>(null);
  const [focus, setFocus] = useState<string | null>(null);
  const clean = useCleanGroupNames(businessId);
  const askedClean = useRef(false);
  const toClean = list.data?.matcher.namesToClean ?? 0;

  // The AI reads the new WhatsApp names once a visit (a sync by the webhook brings them too).
  useEffect(() => {
    if (toClean === 0 || askedClean.current) return;
    askedClean.current = true;
    clean.mutate();
  }, [toClean, clean]);

  useEffect(() => {
    if (!focus || step !== "missing") return;
    const card = document.getElementById(`waiting-${focus}`);
    card?.scrollIntoView({ block: "center", behavior: "smooth" });
    card?.focus?.();
  }, [focus, step]);

  if (list.isLoading) return <SkeletonList rows={4} />;
  if (list.error || !list.data) return <ErrorState error={list.error} onRetry={() => void list.refetch()} />;
  const data = list.data;
  const current = step ?? firstStep(data);

  if (data.waiting.length === 0 && data.linked.length === 0) {
    return (
      <EmptyState
        title="Nothing to link"
        message={`When a ${label("staff")} adds a ${label("participant")}, the name shows here. Ask your ${label("staff", { plural: true })} from the Staff page.`}
      />
    );
  }

  const toLink = data.waiting.filter((w) => !w.participant);
  const openMatches = data.matches;
  const member = label("member");
  const nameless = data.people.filter(unnamed).length;
  const answered = data.claims.filter((c) => c.status === "new").length;
  // Waiting names with an open answer: the "check" part of the bar.
  const toCheck = new Set(data.claims.filter((c) => c.status === "new").flatMap((c) => c.pendingIds)).size;
  const steps: Array<{ id: Step; title: string; hint: string }> = [
    { id: "sync", title: "1. Sync the group", hint: data.people.length ? `${data.people.length} possible ${label("member", { plural: true })}` : "From WhatsApp" },
    { id: "name", title: "2. Name the numbers", hint: `${nameless} without a name` },
    { id: "matches", title: data.matcher.source === "ai" ? "3. AI matches" : "3. Matches", hint: `${openMatches.length} to check` },
    {
      id: "ask",
      title: "4. Ask the parents",
      hint: answered ? `${answered} answered` : data.askParents.postedAt ? "Sent in the group" : "One group post",
    },
    { id: "missing", title: "5. Still missing", hint: `${data.waiting.length} without a ${member}` },
  ];
  const go = (next: Step) => setStep(next);

  return (
    <div className="flex flex-col gap-5">
      <LinkProgressBar
        linked={data.progress.linked}
        waiting={data.progress.waiting}
        check={toCheck}
        participants={label("participant", { plural: true })}
        member={label("member")}
        onShowWaiting={() => go("missing")}
        onShowCheck={() => go("ask")}
      />
      <Tabs
        label="Steps"
        selectedId={current}
        onSelect={(id) => go(id as Step)}
        tabs={steps.map((s) => ({ id: s.id, label: s.title, hint: s.hint }))}
      />
      <div className="grid items-start gap-5 xl:grid-cols-[minmax(0,1fr)_320px]">
        <div className="flex min-w-0 flex-col gap-4" role="tabpanel" id={panelId(current)} aria-labelledby={tabId(current)}>
          {current === "sync" ? (
            <SyncStep businessId={businessId} data={data} cleaning={clean.isPending} onNext={() => go("name")} onSkip={() => go("matches")} />
          ) : null}
          {current === "name" ? <NameStep businessId={businessId} people={data.people} team={data.team} waiting={data.waiting} linked={data.linked} onNext={() => go("matches")} /> : null}
          {current === "matches" ? <MatchesStep businessId={businessId} data={data} onNext={() => go("ask")} /> : null}
          {current === "ask" ? <AskStep businessId={businessId} businessName={businessName} data={data} onNext={() => go("missing")} /> : null}
          {current === "missing" ? <MissingStep businessId={businessId} data={data} toLink={toLink} onAsk={() => go("ask")} /> : null}
        </div>
        <SwimmersPanel
          businessId={businessId}
          waiting={data.waiting}
          linked={data.linked}
          checkIds={new Set(data.claims.filter((c) => c.status === "new").flatMap((c) => c.pendingIds))}
          onOpenWaiting={(id) => {
            setFocus(id);
            go("missing");
          }}
        />
      </div>
    </div>
  );
}

// --- 1. Sync the group ----------------------------------------------------------

type Bucket = "named" | "numbers" | "team";

function SyncStep({
  businessId,
  data,
  cleaning,
  onNext,
  onSkip,
}: {
  businessId: string;
  data: OwnerNewParticipantsResponse;
  cleaning: boolean;
  onNext: () => void;
  onSkip: () => void;
}) {
  const { label } = useLabels();
  const sync = useWhatsAppSync(businessId);
  const syncNow = useSyncNow(businessId);
  const [open, setOpen] = useState(false);
  const [bucket, setBucket] = useState<Bucket>("named");
  const members = label("member", { plural: true });
  const people = data.people;
  const lists: Record<Bucket, GroupPerson[]> = {
    named: people.filter((p) => !unnamed(p)),
    numbers: people.filter(unnamed),
    team: data.team,
  };
  const cleaned = lists.named.filter((p) => p.cleanedFrom).length;
  // The group Teamna stays in: "Sync now" reads it again, with no new add.
  const group = sync.data?.groups.find((g) => g.status === "connected" && g.stay && !g.leftAt) ?? null;
  const connectButton = (
    <Button variant={group ? "ghost" : people.length || data.team.length ? "secondary" : "whatsapp"} onClick={() => setOpen(true)}>
      {group ? "Connect another group" : "Connect the WhatsApp group"}
    </Button>
  );
  const panel = <WhatsAppSyncPanel businessId={businessId} open={open} onClose={() => setOpen(false)} />;

  const syncAgain = (id: string) =>
    syncNow.mutate(id, {
      onSuccess: (result) => showToast(`Synced. ${result.added} new, ${result.left} left the group.`),
      onError: (error) => showToast(messageForError(error), { tone: "info" }),
    });

  if (people.length === 0 && data.team.length === 0) {
    return (
      <section className="flex flex-col gap-4 rounded-card border border-line bg-paper p-4" aria-labelledby="sync-title">
        <div className="flex flex-col gap-1">
          <h2 id="sync-title" className="text-[17px] font-bold text-ink">
            Start with your {members}' WhatsApp group
          </h2>
          <p className="text-[13.5px] text-ink-2">
            Teamna reads the people in the group: their WhatsApp name and number. It sends nothing. Then it takes out your team and helps you match the rest to the{" "}
            {data.waiting.length} {label("participant", { plural: true })}.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          {group ? (
            <Button variant="whatsapp" loading={syncNow.isPending} onClick={() => syncAgain(group.id)}>
              Sync "{group.groupName ?? "the group"}"
            </Button>
          ) : null}
          {connectButton}
          <Button variant="ghost" onClick={onSkip}>
            Skip
          </Button>
        </div>
        {panel}
      </section>
    );
  }

  return (
    <section className="flex flex-col rounded-card border border-line bg-paper" aria-labelledby="sync-title">
      <div className="flex flex-col gap-1 p-4">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <h2 id="sync-title" className="text-[17px] font-bold text-ink">
            {group?.groupName ?? `Your ${members}' WhatsApp group`}
          </h2>
          {group?.lastSyncedAt ? (
            <span className="rounded-full bg-confirmed-surface px-2.5 py-0.5 text-[12px] font-semibold text-confirmed-ink">
              Synced {formatDateTime(group.lastSyncedAt)}
              {group.memberCount !== null ? ` · ${group.memberCount} people` : ""}
            </span>
          ) : null}
        </div>
        <p className="text-[13.5px] text-ink-2">Pick a list to look at. Nothing is saved as a {label("member")} until it is linked to a {label("participant")}.</p>
      </div>
      <fieldset className="grid grid-cols-3 gap-2 px-4 pb-3">
        <legend className="sr-only">Lists</legend>
        <BucketButton
          pressed={bucket === "named"}
          value={lists.named.length}
          text={cleaning ? "With a name. The AI is reading the names" : `With a name.${cleaned ? ` ${cleaned} ${cleaned === 1 ? "name" : "names"} cleaned by AI` : ""}`}
          onClick={() => setBucket("named")}
        />
        <BucketButton pressed={bucket === "numbers"} value={lists.numbers.length} text="Number only, or no real name" onClick={() => setBucket("numbers")} />
        <BucketButton pressed={bucket === "team"} value={lists.team.length} text="Your team, left out" onClick={() => setBucket("team")} />
      </fieldset>
      <ul className="max-h-[380px] overflow-y-auto border-t border-line" aria-label="People">
        {lists[bucket].length === 0 ? <li className="px-4 py-3 text-[13px] text-ink-2">Nobody here.</li> : null}
        {lists[bucket].map((p) => (
          <PersonRow key={p.id} person={p} tag={bucket === "team" ? "Team" : bucket === "numbers" ? "No name" : p.isParent ? label("member", { capital: true }) : null} />
        ))}
      </ul>
      <div className="flex flex-wrap gap-2 border-t border-line p-4">
        <Button onClick={onNext}>Next: name the numbers</Button>
        <Button variant="secondary" onClick={onSkip}>
          {data.matcher.source === "ai" ? "Skip to AI matches" : "Skip to matches"}
        </Button>
        {group ? (
          <Button variant="ghost" loading={syncNow.isPending} onClick={() => syncAgain(group.id)}>
            Sync now
          </Button>
        ) : null}
        {connectButton}
      </div>
      {panel}
    </section>
  );
}

function BucketButton({ pressed, value, text, onClick }: { pressed: boolean; value: number; text: string; onClick: () => void }) {
  return (
    <button
      type="button"
      aria-pressed={pressed}
      onClick={onClick}
      className={`flex flex-col items-start rounded-xl border px-3 py-2 text-left ${pressed ? "border-accent bg-accent-surface" : "border-line bg-paper hover:bg-sunken"}`}
    >
      <span className="text-[22px] font-bold text-ink tabular-nums">{value}</span>
      <span className="text-[12px] leading-snug text-ink-2">{text}</span>
    </button>
  );
}

function PersonRow({ person, tag }: { person: GroupPerson; tag: string | null }) {
  const title = person.name ?? phoneText(person.phoneE164);
  return (
    <li className="flex items-center gap-3 border-b border-line px-4 py-2.5 last:border-b-0">
      <Avatar name={person.name ?? "#"} photoUrl={person.photoUrl} size={32} />
      <div className="min-w-0 flex-1 text-[13.5px]">
        <p className="truncate font-semibold text-ink">{title}</p>
        <p className="truncate text-[12.5px] text-ink-2">
          {person.cleanedFrom ? (
            <>
              <span className="sr-only">WhatsApp name: </span>
              <s>{person.cleanedFrom}</s> ·{" "}
            </>
          ) : null}
          {person.name ? phoneText(person.phoneE164) : person.whatsappName ? `WhatsApp name: ${person.whatsappName}` : "No WhatsApp name"}
        </p>
      </div>
      {tag ? <span className="shrink-0 rounded-full bg-sunken px-2 py-0.5 text-[11.5px] font-bold text-ink-2">{tag}</span> : null}
    </li>
  );
}

// --- 2. Name the numbers ----------------------------------------------------------

function NameStep({
  businessId,
  people,
  team,
  waiting,
  linked,
  onNext,
}: {
  businessId: string;
  people: GroupPerson[];
  /** The team people of the group: never named here, but their numbers count when a number fits two people. */
  team: GroupPerson[];
  /** The names that wait for a parent: a number links to them with no name. */
  waiting: OwnerWaiting[];
  linked: OwnerLinked[];
  onNext: () => void;
}) {
  const { label } = useLabels();
  // The rows stay on screen after a save, so the owner sees what they typed.
  const [ids] = useState(() => people.filter(unnamed).map((p) => p.id));
  const rows = ids.map((id) => people.find((p) => p.id === id)).filter((p): p is GroupPerson => !!p);
  const [drafts, setDrafts] = useState<Record<string, string>>(() =>
    Object.fromEntries(rows.map((p) => [p.id, p.nameEdited ? (p.name ?? "") : ""])),
  );
  // The names the contacts file gave, by person. They wait in the fields until the owner saves them.
  const [fromFile, setFromFile] = useState<Map<string, string>>(new Map());
  // Contacts saved as a relation ("Kareem Mom"): never a name to save, only a hint to find the child.
  // They stay in this browser.
  const [relations, setRelations] = useState<Map<string, { saved: string; child: string | null }>>(new Map());
  const [fileNote, setFileNote] = useState<{ tone: "info" | "warning"; title: string } | null>(null);
  const [linking, setLinking] = useState<string | null>(null);
  const { linkJobs, busy: linkBusy } = useLinkWithUndo(businessId);
  const saveAll = useNameGroupPeople(businessId);
  // A saved name is no longer the file's, at once, not only after the list reloads (review F6.1).
  const forget = (savedIds: string[]) =>
    setFromFile((current) => new Map([...current].filter(([id]) => !savedIds.includes(id))));
  // Only the last file chosen counts: an older read that ends later is ignored (review F4.2).
  const reading = useRef(0);

  // Not saved yet: a name from the file that is still in its field, on a person with no saved name.
  const toSave = rows.flatMap((p) => {
    const name = (drafts[p.id] ?? "").trim();
    return fromFile.has(p.id) && !p.nameEdited && name.length >= 2 ? [{ id: p.id, name }] : [];
  });

  async function readFile(file: File) {
    const turn = ++reading.current;
    // A new file replaces the names of the file before: the fields it filled and not changed go empty.
    // A name saved already (by its row or by "Save N names") stays (review F5.1).
    const saved = new Set(rows.filter((p) => p.nameEdited).map((p) => p.id));
    const previous = new Map([...fromFile].filter(([id]) => !saved.has(id)));
    setFromFile(new Map());
    setRelations(new Map());
    setFileNote(null);
    setDrafts((current) => {
      const next = { ...current };
      for (const [id, name] of previous) if (next[id] === name) next[id] = "";
      return next;
    });
    let text: string;
    try {
      text = await file.text();
    } catch {
      if (turn === reading.current) setFileNote({ tone: "warning", title: "We could not read this file. Choose it again." });
      return;
    }
    if (turn !== reading.current) return;
    const cards: ContactCard[] = parseVcf(text);
    if (cards.length === 0) {
      setFileNote({ tone: "warning", title: "We found no contacts in this file. Export the contacts again as a .vcf file and choose it." });
      return;
    }
    const open = rows.filter((p) => !p.nameEdited);
    // Every person of the group, named or not and the team too, so a local number that fits two people
    // names nobody (review F3.1, F4.1).
    const openIds = new Set(open.map((p) => p.id));
    const matched = [...matchContactNames(cards, [...people, ...team])].filter(([id]) => openIds.has(id));
    // "Kareem Mom" is how the owner knows the number, not the parent's name: the parent sees it when
    // they join. Such a contact helps to find the child, and the parent stays a number.
    const found = new Map(matched.filter(([, name]) => !relationContact(name)));
    const related = new Map(
      matched.flatMap(([id, name]) => {
        const relation = relationContact(name);
        return relation ? [[id, { saved: name, child: relation.child }] as const] : [];
      }),
    );
    track("contacts_file_read", { contact_count: cards.length, matched_count: found.size, relation_count: related.size });
    setFromFile(found);
    setRelations(related);
    setDrafts((current) => {
      const next = { ...current };
      for (const [id, name] of found) if (!(next[id] ?? "").trim() || previous.get(id) === next[id]) next[id] = name;
      return next;
    });
    const relationNote = related.size
      ? `${related.size} ${related.size === 1 ? "is" : "are"} saved by the ${label("participant")}'s name, for example "${[...related.values()][0]!.saved}". Link ${related.size === 1 ? "it" : "them"} without a name.`
      : "";
    setFileNote(
      found.size || related.size
        ? {
            tone: "info",
            title: [
              found.size
                ? `${found.size} of ${open.length} ${open.length === 1 ? "number" : "numbers"} ${found.size === 1 ? "has" : "have"} a name in your contacts. Check the names, then save.`
                : "",
              relationNote,
            ]
              .filter(Boolean)
              .join(" "),
          }
        : {
            tone: "warning",
            title: `The file has ${cards.length} ${cards.length === 1 ? "contact" : "contacts"}, but none of them has a number in this group.`,
          },
    );
  }

  return (
    <section className="flex flex-col rounded-card border border-line bg-paper" aria-labelledby="name-title">
      <div className="flex flex-col gap-1 p-4">
        <h2 id="name-title" className="text-[17px] font-bold text-ink">
          {rows.length ? `Who are these ${rows.length} people?` : "Every person has a name"}
        </h2>
        <p className="text-[13.5px] text-ink-2">
          WhatsApp gives no real name for them. Type the {label("member")}'s name if you know it: Teamna uses it to find the matches.
        </p>
        <p className="text-[13.5px] text-ink-2">
          You do not need a name to link. If you know the {label("participant")} but not the {label("member")}, use "Link without a name". The {label("member")} shows as the
          number until they join Teamna and add their own name.
        </p>
      </div>
      {rows.length ? (
        <div className="flex flex-col gap-3 border-t border-line p-4 sm:flex-row sm:items-center sm:justify-between">
          <div className="min-w-0">
            <p className="text-[14px] font-semibold text-ink">Fill the names from your phone contacts</p>
            <p className="mt-0.5 text-[12.5px] text-ink-2">
              Export your contacts as a .vcf file. iPhone: in Contacts, touch and hold All Contacts, then Export. Android: in Contacts, use Export to file. Teamna reads the file on this computer
              and keeps only the names of the numbers in this group.
            </p>
          </div>
          <FileField
            className="shrink-0"
            label="Contacts file"
            hideLabel
            showFileName={false}
            accept=".vcf,.vcard,text/vcard,text/x-vcard"
            buttonLabel="Choose a .vcf file"
            onFile={(file) => void readFile(file)}
          />
        </div>
      ) : null}
      {fileNote ? (
        <div className="px-4 pb-4">
          <Notice
            tone={fileNote.tone}
            title={fileNote.title}
            action={
              toSave.length ? (
                <Button
                  loading={saveAll.isPending}
                  onClick={() =>
                    saveAll.mutate(toSave, {
                      onSuccess: (result) => {
                        // Only the names the server took (review F7.1). A skipped person got a name another way since.
                        forget(result.namedIds);
                        setFileNote(null);
                        showToast(
                          `${result.named} ${result.named === 1 ? "name" : "names"} saved${result.skipped ? `. ${result.skipped} not saved: they have a name now, or they are not on this list` : ""}`,
                        );
                      },
                      onError: (error) => showToast(messageForError(error), { tone: "info" }),
                    })
                  }
                >
                  {`Save ${toSave.length} ${toSave.length === 1 ? "name" : "names"}`}
                </Button>
              ) : undefined
            }
          />
        </div>
      ) : null}
      {rows.map((p) => (
        <NameRow
          key={p.id}
          businessId={businessId}
          person={p}
          value={drafts[p.id] ?? ""}
          onChange={(value) => setDrafts((current) => ({ ...current, [p.id]: value }))}
          onSaved={() => forget([p.id])}
          fromFile={!p.nameEdited && fromFile.get(p.id) === (drafts[p.id] ?? "")}
          relation={relations.get(p.id)?.saved ?? null}
          linkedNames={linked.filter((l) => l.parentPhoneE164 === p.phoneE164).map((l) => l.name)}
          linking={linking === p.id}
          onLinking={(open) => setLinking(open ? p.id : null)}
        >
          {linking === p.id ? (
            <LinkWithoutName
              person={p}
              waiting={waiting}
              defaultQuery={relations.get(p.id)?.child ?? ""}
              busy={linkBusy}
              onLink={(row) =>
                void linkJobs(
                  // A person who is a parent already (an earlier link) gets the next child in the same family.
                  [{ rows: [row], to: p.isParent ? { kind: "family", familyId: p.id, withoutName: true } : { kind: "contact", contactId: p.id, withoutName: true }, via: "owner" }],
                  `Linked ${row.name} to ${phoneText(p.phoneE164)}.`,
                )
              }
              onClose={() => setLinking(null)}
            />
          ) : null}
        </NameRow>
      ))}
      <div className="p-4">
        <Button onClick={onNext}>Next: matches</Button>
      </div>
    </section>
  );
}

function NameRow({
  businessId,
  person,
  value,
  onChange,
  onSaved,
  fromFile,
  relation,
  linkedNames,
  linking,
  onLinking,
  children,
}: {
  businessId: string;
  person: GroupPerson;
  value: string;
  onChange: (value: string) => void;
  onSaved: () => void;
  /** The field holds the name from the contacts file, not saved yet. */
  fromFile: boolean;
  /** The contacts file has this number as a relation ("Kareem Mom"): shown, never saved. */
  relation: string | null;
  /** The names linked to this number already. */
  linkedNames: string[];
  linking: boolean;
  onLinking: (open: boolean) => void;
  /** The search that links this number with no name, under the row. */
  children?: ReactNode;
}) {
  const { label } = useLabels();
  const name = useNameGroupPerson(businessId);
  const saved = person.nameEdited;
  const save = () => {
    const typed = value.trim();
    if (typed.length < 2) return;
    name.mutate({ id: person.id, name: typed }, { onSuccess: onSaved, onError: (error) => showToast(messageForError(error), { tone: "info" }) });
  };
  return (
    <div className="border-t border-line">
      <div className="grid items-center gap-2 px-4 py-2.5 sm:grid-cols-[minmax(0,1fr)_minmax(0,1.3fr)_auto_auto]">
        <div className="flex min-w-0 items-center gap-3 text-[13.5px]">
          <Avatar name="#" photoUrl={person.photoUrl} size={40} />
          <div className="min-w-0">
            <p className="font-semibold text-ink">{phoneText(person.phoneE164)}</p>
            <p className="text-[12.5px] text-ink-2">
              {relation
                ? `In your contacts as ${relation}`
                : fromFile
                  ? "Name from your contacts"
                  : person.whatsappName
                    ? `WhatsApp name: ${person.whatsappName}`
                    : "No WhatsApp name"}
            </p>
            {linkedNames.length ? <p className="text-[12.5px] font-semibold text-confirmed-ink">✓ Linked to {linkedNames.join(", ")}</p> : null}
          </div>
        </div>
        <CellField
          label={`Name for ${phoneText(person.phoneE164)}`}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") save();
          }}
          placeholder={`${label("member", { capital: true })} name`}
          autoComplete="off"
          // The name cannot change while its save runs, so "Save N names" sends the same name (review F3.2).
          disabled={name.isPending}
          className="w-full"
        />
        {saved && value.trim() === (person.name ?? "") ? (
          <span className="text-[13px] font-semibold text-confirmed-ink">Saved</span>
        ) : (
          <Button variant="secondary" size="sm" loading={name.isPending} disabled={value.trim().length < 2} onClick={save}>
            Save
          </Button>
        )}
        <Button variant={linking ? "ghost" : "secondary"} size="sm" aria-expanded={linking} onClick={() => onLinking(!linking)}>
          Link without a name
        </Button>
      </div>
      {children}
    </div>
  );
}

/**
 * Link a number to a waiting name with no parent name (owner feedback 2026-10-30). The owner knows
 * the child ("Kareem's mom") but not the parent. The parent shows as the number until they join
 * Teamna and add their own name. It stays open after a link, so the owner can link a sibling too.
 */
function LinkWithoutName({
  person,
  waiting,
  defaultQuery,
  busy,
  onLink,
  onClose,
}: {
  person: GroupPerson;
  waiting: OwnerWaiting[];
  defaultQuery: string;
  busy: boolean;
  onLink: (row: OwnerWaiting) => void;
  onClose: () => void;
}) {
  const { label } = useLabels();
  const participant = label("participant");
  const [q, setQ] = useState(defaultQuery);
  // A name that already has an account holder only gets days: that is not a link to a new parent.
  const open = waiting.filter((w) => !w.participant);
  const found = searchByName(open, q, (w) => [w.name, { text: nameParts(w.name, ...w.coachNames), via: true }]);
  useTrackNameSearch("name_step_link", q, found.results);
  const rows = found.searched ? found.rows.slice(0, 6) : [];
  return (
    <div className="flex flex-col gap-2 border-t border-dashed border-line-strong bg-sunken px-4 py-3">
      <p className="text-[13px] text-ink-2">
        Find the {participant} of {phoneText(person.phoneE164)}. The {label("member")} shows as this number until they add their own name.
      </p>
      <SearchField label={`Search a ${participant}`} placeholder={`The ${participant}'s name, or a coach`} value={q} onChange={setQ} className="max-w-md" />
      {rows.map((w) => (
        <PickRow
          key={w.id}
          title={w.name}
          phone={null}
          detail={[w.groupName, w.coachNames[0] ? `added by ${w.coachNames[0].split(" ")[0]}` : ""].filter(Boolean).join(", ")}
          busy={busy}
          onPick={() => onLink(w)}
        />
      ))}
      {found.searched && rows.length === 0 ? <p className="text-[13px] text-ink-2">No waiting {label("participant", { plural: true })} with this name.</p> : null}
      <div>
        <Button variant="ghost" size="sm" onClick={onClose}>
          Close
        </Button>
      </div>
    </div>
  );
}

// --- 3. Matches -------------------------------------------------------------------

function MatchesStep({ businessId, data, onNext }: { businessId: string; data: OwnerNewParticipantsResponse; onNext: () => void }) {
  const { label } = useLabels();
  const run = useRunParentMatches(businessId);
  const { linkJobs, busy } = useLinkWithUndo(businessId);
  const byId = new Map(data.waiting.map((w) => [w.id, w]));
  const rowsOf = (m: ParentMatch) => m.pendingIds.map((id) => byId.get(id)).filter((w): w is OwnerWaiting => !!w && !w.participant);
  const personOf = (m: ParentMatch) => (m.contactId ? data.people.find((p) => p.id === m.contactId) : undefined);
  const likely = data.matches.filter((m) => m.confidence === "likely" && personOf(m));
  const maybe = data.matches.filter((m) => m.confidence === "maybe" && personOf(m));
  const siblings = data.matches.filter((m) => m.confidence === "siblings" || (m.contactId && !personOf(m)));
  const ai = data.matcher.source === "ai";
  const count = (ms: ParentMatch[]) => ms.reduce((n, m) => n + rowsOf(m).length, 0);

  const find = () =>
    run.mutate(undefined, {
      onSuccess: (result) => {
        const found = result.matches.filter((m) => m.contactId).length;
        showToast(found ? `Teamna found ${found} ${found === 1 ? "match" : "matches"}.` : `No ${label("member")} found by name. Look at "Still missing".`, { tone: found ? "success" : "info" });
        if (result.fellBack) showToast("The AI did not answer. Teamna used the family names only.", { tone: "info" });
      },
      onError: (error) => showToast(messageForError(error), { tone: "info" }),
    });

  const linkAllLikely = () =>
    linkJobs(
      likely.map((m) => ({ rows: rowsOf(m), to: { kind: "contact" as const, contactId: personOf(m)!.id }, via: "match" as const, matchId: m.id })),
      `Linked ${count(likely)} ${label("participant", { plural: true })}.`,
    );

  return (
    <section className="flex flex-col gap-4" aria-labelledby="matches-title">
      <div className="flex flex-col gap-3 rounded-card border border-line bg-paper p-4">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="flex min-w-0 flex-col gap-1">
            <div className="flex flex-wrap items-center gap-2">
              <h2 id="matches-title" className="text-[17px] font-bold text-ink">
                {ai ? "AI matches" : "Matches"}
              </h2>
              {ai && data.matcher.lastRunAt ? (
                <span className="rounded-full bg-accent-surface px-2.5 py-0.5 text-[12px] font-semibold text-accent-strong">
                  Read {data.people.filter((p) => !unnamed(p)).length} names
                </span>
              ) : null}
            </div>
            <p className="text-[13.5px] text-ink-2">
              {ai
                ? `The AI reads the names of the ${label("participant", { plural: true })} and of the people in the group, never the numbers. It knows naming customs, nicknames and other scripts. Check each match. A link places the ${label("participant")} on the ${label("staff")}'s days.`
                : `Teamna looks for the family name of each ${label("participant")} in the names of the group. Check each match.`}
            </p>
            {data.matcher.lastRunAt ? <p className="text-[12.5px] text-ink-3">Last looked {formatDateTime(data.matcher.lastRunAt)}.</p> : null}
          </div>
          <Button variant={data.matcher.lastRunAt ? "secondary" : "primary"} loading={run.isPending} onClick={find}>
            {data.matcher.lastRunAt ? "Look again" : `Find the ${label("member", { plural: true })}`}
          </Button>
        </div>
      </div>

      {likely.length ? (
        <MatchGroup
          title={`Likely · ${count(likely)} ${label("participant", { plural: true })}`}
          action={
            <Button variant="primary" size="sm" loading={busy} onClick={() => void linkAllLikely()}>
              Link all likely
            </Button>
          }
        >
          {likely.map((m) => (
            <MatchCard key={m.id} businessId={businessId} match={m} rows={rowsOf(m)} person={personOf(m)!} data={data} />
          ))}
        </MatchGroup>
      ) : null}
      {maybe.length ? (
        <MatchGroup title="Maybe · check these">
          {maybe.map((m) => (
            <MatchCard key={m.id} businessId={businessId} match={m} rows={rowsOf(m)} person={personOf(m)!} data={data} />
          ))}
        </MatchGroup>
      ) : null}
      {siblings.length ? (
        <MatchGroup title={`Siblings, no ${label("member")} found`}>
          {siblings.map((m) => (
            <MatchCard key={m.id} businessId={businessId} match={m} rows={rowsOf(m)} data={data} />
          ))}
        </MatchGroup>
      ) : null}
      {data.matcher.lastRunAt && !likely.length && !maybe.length && !siblings.length ? (
        <Notice tone="info" title="No open matches">
          Every match is linked or dismissed. The {label("participant", { plural: true })} left are in "Still missing".
        </Notice>
      ) : null}
      <div>
        <Button variant="secondary" onClick={onNext}>
          Next: still missing
        </Button>
      </div>
    </section>
  );
}

function MatchGroup({ title, action, children }: { title: string; action?: ReactNode; children: ReactNode }) {
  return (
    <div className="flex flex-col gap-2">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="eyebrow text-ink-3">{title}</p>
        {action}
      </div>
      {children}
    </div>
  );
}

function MatchCard({
  businessId,
  match,
  rows,
  person,
  data,
}: {
  businessId: string;
  match: ParentMatch;
  rows: OwnerWaiting[];
  person?: GroupPerson;
  data: OwnerNewParticipantsResponse;
}) {
  const { label } = useLabels();
  const { linkNames, busy } = useLinkWithUndo(businessId);
  const dismiss = useDismissParentMatch(businessId);
  const photoOf = useStaffPhotos(businessId);
  const [picking, setPicking] = useState(false);
  if (rows.length === 0) return null;
  const names = rows.map((r) => r.name).join(", ");
  const firsts = rows.map((r) => r.name.split(" ")[0]).join(" and ");
  const tone = match.confidence === "likely" ? "border-l-confirmed" : match.confidence === "maybe" ? "border-l-late" : "border-l-line-strong";
  return (
    <article className={`flex flex-col overflow-hidden rounded-card border border-l-4 border-line bg-paper ${tone}`} aria-label={names}>
      <div className="flex flex-col gap-1.5 px-4 py-3">
        <p className="flex flex-wrap items-center gap-x-2 gap-y-1 text-[14.5px] text-ink">
          <b>{names}</b>
          {person ? (
            <>
              <span className="text-ink-3" aria-hidden="true">
                →
              </span>
              <span>
                {personLabel(person)} <span className="text-[12.5px] text-ink-2">{phoneText(person.phoneE164)}</span>
              </span>
            </>
          ) : null}
          <span className="rounded-full bg-sunken px-2 py-0.5 text-[11.5px] font-bold text-ink-2">
            {match.confidence === "likely" ? "Likely" : match.confidence === "maybe" ? "Maybe" : "Siblings"}
          </span>
        </p>
        <p className="text-[12.5px] text-ink-3">
          {rows[0]!.groupName}
          {rows[0]!.coachNames.map((name) => (
            <Fragment key={name}>
              {" · "}
              <AvatarName name={name} photoUrl={photoOf(null, name)} size={18} />
            </Fragment>
          ))}
        </p>
        <p className="flex items-baseline gap-1.5 text-[13px] text-ink-2">
          <span className="rounded bg-accent-surface px-1.5 text-[10.5px] font-extrabold text-accent-strong">{match.source === "ai" ? "AI" : "Rule"}</span>
          {match.reason}
        </p>
        {!picking ? (
          <div className="flex flex-wrap gap-2 pt-1">
            {person ? (
              <Button
                size="sm"
                loading={busy}
                onClick={() => void linkNames(rows, { kind: "contact", contactId: person.id }, { via: "match", matchId: match.id, done: `${firsts} linked to ${personLabel(person).split(" ")[0]}.` })}
              >
                Link
              </Button>
            ) : null}
            <Button variant={person ? "secondary" : "primary"} size="sm" onClick={() => setPicking(true)}>
              Pick a {label("member")}
            </Button>
            {/* Every match can be rejected, a sibling grouping too (review F12.1). */}
            <Button
              variant="ghost"
              size="sm"
              loading={dismiss.isPending}
              onClick={() => dismiss.mutate(match.id, { onError: (error) => showToast(messageForError(error), { tone: "info" }) })}
            >
              {person ? "Not right" : "Not siblings"}
            </Button>
          </div>
        ) : null}
      </div>
      {picking ? (
        <ParentPicker
          parents={data.parents}
          people={data.people}
          busy={busy}
          onCancel={() => setPicking(false)}
          onPick={(picked) => {
            setPicking(false);
            void linkNames(rows, picked.target, { via: "owner", done: `${firsts} linked to ${picked.name.split(" ")[0]}.` });
          }}
        />
      ) : null}
    </article>
  );
}

// --- 4. Still missing -------------------------------------------------------------

function MissingStep({ businessId, data, toLink, onAsk }: { businessId: string; data: OwnerNewParticipantsResponse; toLink: OwnerWaiting[]; onAsk: () => void }) {
  const { label } = useLabels();
  const ask = useAskParents(businessId);
  const byName = claimsByName(data.claims);
  const pairs = claimPairs(data.claims);
  const toCheck = data.waiting.filter((w) => (byName.get(w.id) ?? []).length > 0).length;
  const posted = data.askParents.postedAt !== null;
  const copyReminder = async () => {
    try {
      await navigator.clipboard.writeText(fullGroupMessage(REMINDER_MESSAGE, data.askParents.findUrl));
    } catch {
      // Not recorded: only a real copy counts as reminded (review F2.2).
      showToast("Copy did not work here. Open step 4 to copy the text by hand.", { tone: "info" });
      return;
    }
    ask.mutate("reminder", { onError: (error) => showToast(messageForError(error), { tone: "info" }) });
    showToast("Copied the reminder. Paste it in the WhatsApp group.");
  };
  if (data.waiting.length === 0) return <AllLinked businessId={businessId} data={data} />;
  const matchOf = new Map<string, ParentMatch>();
  for (const m of data.matches) for (const id of m.pendingIds) if (!matchOf.has(id)) matchOf.set(id, m);
  const waitingIds = new Set(toLink.map((w) => w.id));
  // A sibling card shows once: on the first name of the group, with "Link ... too".
  const shown = new Set<string>();
  const cards: Array<{ row: OwnerWaiting; siblings: OwnerWaiting[] }> = [];
  for (const row of data.waiting) {
    if (shown.has(row.id)) continue;
    const match = matchOf.get(row.id);
    const siblings = match && match.pendingIds.length > 1 ? data.waiting.filter((w) => w.id !== row.id && match.pendingIds.includes(w.id) && waitingIds.has(w.id)) : [];
    siblings.forEach((s) => shown.add(s.id));
    cards.push({ row, siblings });
  }
  return (
    <section className="flex flex-col gap-3" aria-label="Still missing">
      <div className="flex flex-col gap-1 rounded-card border border-line bg-paper p-4">
        <h2 className="text-[17px] font-bold text-ink">
          {data.waiting.length} {data.waiting.length === 1 ? label("participant") : label("participant", { plural: true })} still without a {label("member")}
        </h2>
        <p className="text-[13.5px] text-ink-2">
          {toCheck ? `${toCheck} have an answer from a ${label("member")} to check. ` : ""}For the others, here is what Teamna tried, and what you can do next.
        </p>
        <div className="flex flex-wrap items-center gap-3 pt-2">
          {posted ? (
            data.askParents.remindedAt ? (
              <span className="rounded-full bg-confirmed-surface px-2.5 py-0.5 text-[12px] font-semibold text-confirmed-ink">Reminder copied</span>
            ) : (
              <>
                <Button variant="whatsapp" size="sm" loading={ask.isPending} onClick={() => void copyReminder()}>
                  Copy a reminder
                </Button>
                <span className="text-[12.5px] text-ink-2">Send it in the group from your WhatsApp. It names no children.</span>
              </>
            )
          ) : (
            <Button variant="whatsapp" size="sm" onClick={onAsk}>
              Ask the {label("member", { plural: true })} in the group first
            </Button>
          )}
        </div>
      </div>
      {cards.map(({ row, siblings }) => (
        <WaitingCard
          key={row.id}
          businessId={businessId}
          row={row}
          parents={data.parents}
          people={data.people}
          match={matchOf.get(row.id)}
          siblings={siblings}
          ai={data.matcher.source === "ai"}
          looked={data.matcher.lastRunAt !== null}
          claims={data.claims.filter((c) => [row, ...siblings].some((r) => c.pendingIds.includes(r.id)))}
          pairs={pairs}
          waiting={data.waiting}
          posted={posted}
          coachAsk={data.coachAsks.find((a) => a.pendingIds.includes(row.id))}
        />
      ))}
    </section>
  );
}

/**
 * The last step when every name has a parent: invite the parents linked here who have no invite
 * yet (the Families page invite: one WhatsApp message each, nothing for a parent in the app).
 */
function AllLinked({ businessId, data }: { businessId: string; data: OwnerNewParticipantsResponse }) {
  const { label } = useLabels();
  const invite = useInviteFamilies(businessId);
  const [confirming, setConfirming] = useState(false);
  const familyIds = [...new Set(data.linked.filter((l) => l.familyId && l.parentInvite === "none").map((l) => l.familyId!))];
  const n = familyIds.length;
  const parents = n === 1 ? label("member") : label("member", { plural: true });
  const send = () =>
    invite.mutate(
      // The server sends only to adults with no invite yet, whatever this screen read (review F1.2).
      { familyIds, onlyNew: true },
      {
        onSuccess: (result) => {
          setConfirming(false);
          showToast(
            result.sent === 0
              ? `These ${label("member", { plural: true })} have an invite already. Nothing was sent.`
              : result.simulated
                ? `${result.families} invites recorded. WhatsApp is not connected, so nothing was sent.`
                : `Invited ${result.families} ${result.families === 1 ? label("member") : label("member", { plural: true })}.`,
          );
        },
        onError: (error) => showToast(messageForError(error), { tone: "info" }),
      },
    );
  return (
    <section className="flex flex-col gap-3 rounded-card border border-line bg-paper p-4" aria-labelledby="all-linked-title">
      <h2 id="all-linked-title" className="text-[17px] font-bold text-ink">
        Every {label("participant")} has a {label("member")}
      </h2>
      <p className="text-[13.5px] text-ink-2">
        {n
          ? `Next: invite the ${label("member", { plural: true })}. Each one gets one WhatsApp message with a link to sign in.`
          : `The ${label("member", { plural: true })} you linked here have an invite already. You can send a reminder from the ${label("member", { plural: true, capital: true })} page.`}
      </p>
      {n ? (
        <div>
          <Button variant="whatsapp" onClick={() => setConfirming(true)}>
            Invite {n} {parents}
          </Button>
        </div>
      ) : null}
      <ConfirmDialog
        open={confirming}
        title={`Invite ${n} ${parents}?`}
        confirmLabel={`Send ${n} ${n === 1 ? "invite" : "invites"}`}
        loading={invite.isPending}
        onConfirm={send}
        onCancel={() => setConfirming(false)}
      >
        Teamna sends one WhatsApp message to each {label("member")}, with a link to sign in. A {label("member")} who is in the app already gets nothing.
      </ConfirmDialog>
    </section>
  );
}
